import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import CustomButton from "../Buttons/CustomButton";
import EditPencil from "../Icons/EditPencil";
import BigRightArrow from "../Icons/BigRightArrow";
import BigLeftArrow from "../Icons/BigLeftArrow";
import CandidateVacancyCard from "../../Candidate/Jobs/CandidateVacancyCard";
import Warning from "../Icons/Warning";
import { resetCandidateDayCalendarData } from "../../../store/candidate/slice";
import { TDayData, TJobData } from "../../../store/candidate/types";
import Checkbox from "../Inputs/Checkbox";
import Alert from "../Icons/Alert";
import EditSingleDay from "../../Candidate/Availabilities/EditSingleDay";
import CalendarVacancyCard from "../../Candidate/Jobs/CalendarVacancyCard";
import ConfirmApplyJobModal from "../../Candidate/Jobs/JobListings/ConfirmApplyJobModal";
import ViewCentre from "../../SuperAdmin/CentresAndUsers/Centres/ViewCentre";
import { hideTransparentPreloaderAction, showTransparentPreloaderAction } from "../../../store/application/slice";
import { getCentreDetailsThunk } from "../../../store/superadmin/thunks";
import JobAcceptedConfirmationModal from "../../Candidate/Jobs/JobListings/JobAcceptedConfirmation";

interface CalendarAcceptedJobsProps {
  singleDayData: TDayData;
  currentSelectedDay: string; // Assuming this is a date string like "2025-06-09"
  dayKey: string;
  onRefresh: () => void;
  onClickClose: () => void;
  handleCellChange: (weekKey: string, day: string, checked: boolean) => void;
  weekName: string;
  setCurrentSelectedDay?: (day: string) => void;
  formatAvailabilities?: (availabilities: any) => any;
  setShowAcceptedJobs?: (show: boolean) => void;
  loadDayJobs?: () => void;
  selectedDay?: TDayData | null; // Optional prop to pass the selected day
}

interface CandidateDayCalendarJob {
  is_user_step_exists: boolean;
  // Add other properties of vacancy as needed
  [key: string]: any;
}

const CalendarSingDayView: React.FC<CalendarAcceptedJobsProps> = ({
  weekName,
  singleDayData,
  currentSelectedDay,
  setCurrentSelectedDay,
  dayKey,
  onClickClose,
  onRefresh,
  handleCellChange,
  formatAvailabilities,
  setShowAcceptedJobs,
  loadDayJobs,
  selectedDay
}) => {
  const dispatch = useAppDispatch();
  const calendarDates = useAppSelector(
    (state) => state.candidateJobs.calendarDates
  );
  const candidateDayCalendarJobs = useAppSelector(
    (state) => state.candidateJobs.candidateDayCalendarData
  );
  // Compute selectedDayIndex dynamically inside the component render
  const selectedDayIndex = calendarDates.findIndex(
    (item) => item.date === currentSelectedDay
  );

  const [showAcceptJobModal, setShowAcceptJobModal] = useState(false);
  const [showConflictConfirmModal, setShowConflictConfirmModal] =
    useState(false);
  const [showConfirmApplyJobModal, setShowConfirmApplyJobModal] =
    useState(false);
  const [showJobAcceptedConfirmation, setShowJobAcceptedConfirmation] =
    useState(false);
  const [showCentreViewModal, setShowCentreViewModal] =
    useState<boolean>(false);
  const [showWithdrawApplyJobModal, setShowWithdrawApplyJobModal] =
    useState(false);
  const [showDeclineApplyJobModal, setShowDeclineApplyJobModal] =
    useState(false);

  const [selectedVacancy, setSelectedVacancy] = useState<TJobData | null>(
    null);

  const [showEditSingleAvailability, setShowEditSingleAvailability] =
    useState<boolean>(false);
  const dayData =
    calendarDates.filter((item) => item.date === currentSelectedDay)?.[0] ||
    singleDayData;
  // Define a type for dayAvailability if not already defined
  interface DayAvailability {
    date: string;
    all_day_available?: number;
    [key: string]: any;
  }

  const dayAvailability = dayData?.availability
    ? (Object.values(dayData.availability)[0] as DayAvailability)
    : undefined;
  const listingType = dayData?.accepted_count > 0 ? "accepted" : "listing";
  const isDayEmpty =
    dayData?.listing_count === 0 && dayData?.accepted_count === 0 && (dayAvailability && dayAvailability.all_day_available !== 0);

  const getDayFromDate = (dateStr: string): string => {
    // Assumes format "25 Dec 2025"
    const [day] = dateStr.split(" ");
    return day;
  };

  const getMonthFromDate = (dateStr: string): string => {
    // Assumes format "25 Dec 2025"
    const [, mon] = dateStr.split(" ");
    return mon || "";
  };

  let hidePreviousButton = false;
  let hideNextButton = false;
  const previousDate = calendarDates[selectedDayIndex - 1]?.date;
  const prevDateObj = new Date(previousDate);
  const todayDate = new Date();
  if (
    prevDateObj <
    new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate())
  ) {
    hidePreviousButton = true;
  }

  if (selectedDayIndex === calendarDates.length - 1) {
    hideNextButton = true;
  }

  const handlePrevDate = async () => {
    if (selectedDayIndex > 0) {
      const prevDate = calendarDates[selectedDayIndex - 1].date;
      const today = new Date();
      const prevDateObj = new Date(prevDate);

      // Only call API if prevDate is today or in the future
      if (
        prevDateObj >=
        new Date(today.getFullYear(), today.getMonth(), today.getDate())
      ) {
        setCurrentSelectedDay && setCurrentSelectedDay(prevDate);
        await dispatch(resetCandidateDayCalendarData());
      }
    }
  };

  const handleNextDate = async () => {
    if (selectedDayIndex < calendarDates.length - 1) {
      await dispatch(resetCandidateDayCalendarData());
      setCurrentSelectedDay &&
        setCurrentSelectedDay(calendarDates[selectedDayIndex + 1].date);
    }
  };

  const handleCentreView = async (id: number) => {
    setShowCentreViewModal(true);
    dispatch(showTransparentPreloaderAction());
    await dispatch(getCentreDetailsThunk({ centre_id: id }));
    dispatch(hideTransparentPreloaderAction());
  };

  return (
    <>
      <div className="pt-50px">
        <div className={`mobile-title px-30px py-11px d-flex align-items-center justify-content-center h-60px ${dayData?.accepted_count > 0 ? "bg-teal-light" : (dayAvailability && dayAvailability.all_day_available !== 0) ? "bg-white" : "bg-off-white"}`}>
          <div className="min-w-200px mobile-w-100 d-flex gap-15 align-items-center justify-content-between">
            <div
              className={`cursor-pointer`}
              onClick={!hidePreviousButton ? handlePrevDate : undefined}
            >
              <BigLeftArrow disabled={hidePreviousButton} />
            </div>
            <div className="d-flex gap-5 gap-mobile-15 align-items-center">
              <div className="fs-22 fs-mobile-40 fw-400 text-black">
                {getDayFromDate(currentSelectedDay)}
              </div>
              <div className="fs-16 fs-mobile-30 fw-400 text-black">
                {getMonthFromDate(currentSelectedDay)}
              </div>
            </div>
            <div className="cursor-pointer" onClick={handleNextDate}>
              <BigRightArrow disabled={hideNextButton} />
            </div>
          </div>
        </div>
        {!showEditSingleAvailability && (
          <div className="d-flex flex-column gap-15  mobile-job-scroll">
            <div className="d-flex gap-10 align-items-center justify-content-between">
              {dayAvailability && typeof dayAvailability === "object" && "date" in dayAvailability && (
                <div className="theme-check">
                  <Checkbox
                    className="theme-check-input"
                    id={(dayAvailability as { date: string }).date}
                    checked={!!dayAvailability.all_day_available}
                    onChange={(checked) =>
                      handleCellChange(weekName, dayAvailability?.day_of_week, checked)
                    }
                    label={
                      dayAvailability.all_day_available === 1
                        ? "Available all day"
                        : dayAvailability.all_day_available === 0
                        ? "Not available"
                        : dayAvailability.all_day_available === 2
                        ? formatAvailabilities &&
                          formatAvailabilities(dayData.availability)
                        : ""
                    }
                    themeCheck
                    labelClass={`${ !!dayData?.accepted_count ? "checked-grey-light" : "checked-teal" } fs-16 ${dayAvailability.all_day_available === 0 ? `text-grey-medium` : `text-teal-dark`}`}
                    disabled={!!dayData?.accepted_count}
                  />
                </div>
              )}
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-transparent text-grey-medium py-0"
                onClick={() => setShowEditSingleAvailability(true)}
              >
                <EditPencil color="#757575" />
                Edit
              </CustomButton>
            </div>
            <div className="bg-off-white min-h-1px"></div>

            <>
              {(dayData?.accepted_count === 0 && (dayAvailability && dayAvailability.all_day_available !== 0)) && (
                <>
                  <div className="fs-16 fw-400 text-grey-medium">
                    No jobs scheduled
                  </div>
                  <div className="bg-off-white min-h-1px"></div>
                </>
              )}

              {isDayEmpty && (
                <div className="d-flex gap-5">
                  <Warning />
                  <div className="d-flex flex-column">
                    <div className="fs-16 fw-400 text-black">
                      <span className="fw-700">Quick Tip:</span> If you require
                      a job today, here are some tips on increasing your
                      matches:
                    </div>
                    <ul className="fs-16 fw-400 text-black">
                      <li>Broaden your job locations</li>
                      <li>Extend your available timeframes</li>
                      <li>
                        Accept jobs at other levels that you are still qualified
                        for
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </>

            {listingType === "listing" && dayData?.listing_count > 0 && (
              <div className="d-flex gap-5 align-items-center fs-16 fw-700 text-alert">
                <Alert />
                {dayData?.listing_count} job matches found
              </div>
            )}

            {candidateDayCalendarJobs &&
              (candidateDayCalendarJobs as CandidateDayCalendarJob[]).length >
                0 &&
              (candidateDayCalendarJobs as CandidateDayCalendarJob[]).map(
                (vacancy: any, index: number) =>
                  listingType === "accepted" &&
                  candidateDayCalendarJobs.length === 0 ? (
                    <div key={index}>
                      <div className="bg-off-white h-1px"></div>
                      <div className="fs-16 fw-400 text-grey-medium">
                        No jobs scheduled
                      </div>
                      <div className="bg-off-white h-1px"></div>
                    </div>
                  ) : listingType === "listing" &&
                    candidateDayCalendarJobs.length === 0 ? (
                    <div key={index} className="d-flex gap-5">
                      <Warning />
                      <div className="d-flex flex-column">
                        <div className="fs-16 fw-400 text-black">
                          <span className="fw-700">Quick Tip:</span> If you
                          require a job today, here are some tips on increasing
                          your matches:
                        </div>
                        <ul className="fs-16 fw-400 text-black">
                          <li>Broaden your job locations</li>
                          <li>Extend your available timeframes</li>
                          <li>
                            Accept jobs at other levels that you are still
                            qualified for
                          </li>
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <>
                      <CalendarVacancyCard
                        key={index}
                        vacancy={vacancy}
                        type={listingType === "listing" ? "jobs" : "accepted"}
                        isCandidateFilledAllForms={true}
                        isMobile={true}
                        dayAvailability={dayAvailability}
                        setShowAcceptedJobs={setShowAcceptedJobs}
                        // Pass all state and setState as props
                        showAcceptJobModal={showAcceptJobModal}
                        setShowAcceptJobModal={setShowAcceptJobModal}
                        showConflictConfirmModal={showConflictConfirmModal}
                        setShowConflictConfirmModal={
                          setShowConflictConfirmModal
                        }
                        showConfirmApplyJobModal={showConfirmApplyJobModal}
                        setShowConfirmApplyJobModal={
                          setShowConfirmApplyJobModal
                        }
                        showWithdrawApplyJobModal={showWithdrawApplyJobModal}
                        setShowWithdrawApplyJobModal={
                          setShowWithdrawApplyJobModal
                        }
                        showDeclineApplyJobModal={showDeclineApplyJobModal}
                        setShowDeclineApplyJobModal={
                          setShowDeclineApplyJobModal
                        }
                        showCentreViewModal={showCentreViewModal}
                        setShowCentreViewModal={setShowCentreViewModal}
                        onRefresh={onRefresh}
                        index={index}
                        setSelectedVacancy={setSelectedVacancy}
                        selectedVacancy={selectedVacancy}
                        setShowJobAcceptedConfirmation={setShowJobAcceptedConfirmation}
                        showJobAcceptedConfirmation={showJobAcceptedConfirmation}
                       
                      />
                      
                    </>
                  )
              )}
          </div>
        )}

        {showEditSingleAvailability && (
          <EditSingleDay
            dayData={singleDayData}
            dayKey={dayKey}
            onRefresh={onRefresh}
            onClickClose={onClickClose}
            isMobile={true}
            setShowEditSingleAvailability={setShowEditSingleAvailability}
          />
        )}

        {
          selectedVacancy && (
            <ConfirmApplyJobModal
              showApplyJobModal={!!showConfirmApplyJobModal}
              setShowApplyJobModal={setShowConfirmApplyJobModal}
              vacancyData={selectedVacancy}
               loadDayJobs={loadDayJobs}
               onRefresh={onRefresh}
            />
          )
        }

        {selectedVacancy && (
            <JobAcceptedConfirmationModal
              showApplyJobModal={!!showJobAcceptedConfirmation}
              setShowApplyJobModal={setShowJobAcceptedConfirmation}
              vacancyData={selectedVacancy}
              loadDayJobs={loadDayJobs}
              onRefresh={onRefresh}
            />
        )}

        {
          showCentreViewModal && (
            <ViewCentre
              showViewModal={showCentreViewModal}
              setShowViewModal={setShowCentreViewModal}
              setShowAcceptedJobs={setShowAcceptedJobs}
              onRefresh={onRefresh}
              handleView={handleCentreView}
              showSingleDayView={setShowAcceptedJobs}
            />
          )
        }
        
      </div>
    </>
  );
};

export default CalendarSingDayView;
